/**
 * Agent Teams (P5.1–P5.3) — the teammate / helper wire.
 *
 * One domain's payload types: a teammate or background helper being spawned, its
 * progress, and the messages that flow between a lead and its peers. Extracted from
 * types/events.ts, which re-exports every name below, so no import path changed.
 *
 * It lives in its own module for the reason the file-size ratchet (R3) states —
 * new work goes into a new module rather than growing the one file everything
 * imports — and because this is the one event family in types/events.ts that never
 * got the sibling module its neighbours have (credits.ts, browser-stream.ts,
 * task.ts, presence.ts, story.ts, board-stream.ts, live-preview.ts).
 *
 * The spawned and message payloads DERIVE from their zod schemas (the single author of
 * the wire); the imports are type-only, so importing a teammate payload still drags no
 * runtime graph in behind it.
 */

import type { z } from 'zod';
import type { SubagentMessagePayloadSchema, SubagentSpawnedPayloadSchema } from '../schemas/event-payloads.schema.js';

/**
 * P5.1 (Agent Teams) — a teammate task was spawned via the Agent tool in
 * mode:'teammate'. The teammate is a task-scoped actor with its own
 * taskId/actor/journal/billing; this event lets the parent's render host open a
 * lane for it. Dark behind the AGENT_TEAMS flag (default OFF).
 */
export type SubagentSpawnedPayload = z.infer<typeof SubagentSpawnedPayloadSchema>;

/**
 * P5.1 (Agent Teams) — a message flowing between a lead and a teammate. The
 * `taskId` is the PERSIST-TARGET (the recipient's thread); `peerTaskId` is the
 * other side. `messageId` is client-minted for dedup/optimistic reconcile.
 */
/**
 * Phase 2 (spec §4.7, D11) — why a background helper's REPORT arrived. A closed
 * set: the report leg mints exactly these (teammates.ts), and a consumer
 * branching on the value must get a closed set rather than a bare string. Stated
 * here rather than reusing the engine's `SubagentStopReason`, which is a wider,
 * different set: `refused` is a run outcome that never reaches a report.
 *
 * `credits_exhausted` is held apart from `stopped_by_user` because they are not
 * the same event. A credit kill takes the same path a Stop does, so the lead used
 * to read every one of them as the person calling the helper off (founder
 * 2026-09-05, "from my stop -> out of credits").
 *
 * Phase 3 (§4.9) adds `stopped_by_parent` for the same reason, from the other
 * side: the LEAD stopping its own helper (`StopHelper`) is neither the person's
 * Stop nor a credit kill, and the person must not read "Helper stopped" for a
 * call Bash made.
 *
 * The zod mirror stays `z.string().optional()` on purpose (R89), so this is the
 * contract the PRODUCERS honour, not one the relay enforces: a helper always
 * reports, and dropping a whole report because one field carried an unexpected
 * value is the worse failure. A consumer that must survive an unknown value reads
 * it as the string it is (apps/web's PeerAttribution does exactly that).
 */
export type SubagentReportStopReason = 'completed' | 'stopped_by_user' | 'stopped_by_parent' | 'turn_cap' | 'failed' | 'credits_exhausted';

/**
 * Phase 3 (spec §4.5, R25) — the LEAD's handle on the peer wire. The lead has no
 * teammate name of its own, so this one word is both the `from` its messages carry
 * and the `peerName` on their origin. It lives HERE because three layers compare
 * against it: the runtime that mints the origin, the delivery that chooses between
 * "Your lead added" and a helper's prefix, and the web's row label. A literal in
 * any one of them is a rename waiting to split the three apart.
 */
export const LEAD_PEER_HANDLE = 'lead';

// The schema keeps `stopReason` a bare string on purpose (R89: a helper always reports);
// the type narrows it to the closed set the producers mint, and nothing else.
export type SubagentMessagePayload = Omit<z.infer<typeof SubagentMessagePayloadSchema>, 'stopReason'> & {
  stopReason?: SubagentReportStopReason;
};
